import React, { useMemo } from "react";
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";

interface MapConProps {
  markers: Array<{ lat: number | null; lng: number | null; title?: string; type?: 'active' | 'inactive' }>;
  center: { lat: number; lng: number };
  zoom?: number;
  mapStyle?: React.CSSProperties;
}

const GOOGLE_MAPS_API_KEY = "AIzaSyB1rIsa_A5keNvPCSqAzVsY92LgLRmfTmI";
const libraries: ["places"] = ["places"]; 

const mapStyles = [
  {
    featureType: "all",
    elementType: "labels.text.fill",
    stylers: [{ color: "#7c93a3" }, { lightness: "-10" }]
  },
  {
    featureType: "administrative.country",
    elementType: "geometry",
    stylers: [{ visibility: "simplified" }, { color: "#e6e6e6" }]
  },
  {
    featureType: "landscape",
    elementType: "geometry.fill",
    stylers: [{ color: "#f5f5f5" }]
  },
  {
    featureType: "road",
    elementType: "geometry.fill",
    stylers: [{ color: "#ffffff" }]
  },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [{ weight: 0.5 }, { color: "#e9e9e9" }]
  },
  {
    featureType: "water",
    elementType: "geometry.fill",
    stylers: [{ color: "#a3ccdf" }, { opacity: 0.5 }]
  }
];

const MapCon: React.FC<MapConProps> = ({ 
  markers, 
  center, 
  zoom = 10,
  mapStyle 
}) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries
  });

  const mapMarkers = useMemo(() => 
    markers
      .filter(marker => 
        marker.lat != null && 
        marker.lng != null && 
        !isNaN(Number(marker.lat)) && 
        !isNaN(Number(marker.lng))
      )
      .map(({ lat, lng, title, type = 'active' }) => ({
        lat: Number(lat),
        lng: Number(lng),
        title: title || '',
        type,
        key: `${lat}-${lng}`
      })),
    [markers]
  );

  const mapContainerStyle = {
    width: "100%",
    height: "400px",
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
    overflow: 'hidden',
    ...mapStyle
  };

  // Create SVG pins that match Google Maps style but smaller
  const createPinSVG = (color: string) => {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="36" viewBox="0 0 384 512">
      <path fill="${color}" d="M192 0C86 0 0 86 0 192c0 77.4 27 99 172.3 309.7c9.5 13.8 29.9 13.8 39.4 0C357 291 384 269.4 384 192C384 86 298 0 192 0zm0 288c-53 0-96-43-96-96s43-96 96-96s96 43 96 96s-43 96-96 96z"/>
    </svg>`;
  };

  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) return <div className="animate-pulse bg-gray-200 h-[400px] w-full rounded-xl"></div>;

  return (
    <div className="relative w-full">
      <GoogleMap 
        mapContainerStyle={mapContainerStyle} 
        zoom={zoom} 
        center={center}
        options={{
          styles: mapStyles,
          disableDefaultUI: true,
          zoomControl: true,
          streetViewControl: false,
          mapTypeControl: false,
        }}
      >
        {mapMarkers.map(({ lat, lng, key, title, type }) => (
          <Marker 
            key={key} 
            position={{ lat, lng }}
            icon={{
              url: 'data:image/svg+xml;utf8,' + encodeURIComponent(
                createPinSVG(type === 'active' ? '#75A7AD' : '#9CA3AF')
              ),
              scaledSize: new window.google.maps.Size(24, 36), // Reduced from 32x48 to 24x36
              anchor: new window.google.maps.Point(12, 36), // Adjusted anchor point for new size
            }}
            title={title}
          />
        ))}
      </GoogleMap>
    </div>
  );
};

export default React.memo(MapCon);

